// lib
import React, { useReducer, useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Transition } from 'react-transition-group'
import { isEqual } from 'lodash'

import ADND from '@aytm/lib/adnd/ADND'
import BasicFilters from '@aytm/app/stats/lib/basic-filters'
import { dataReducer } from '@aytm/app/stats/page/question-views/vq/reducers'

import { generateUID } from '@aytm/app/stats/lib/helpers'
import { getLogicData, getRefs, getClustersRefs } from '@aytm/app/stats/lib/refs'

// components
import { default as ComponentContainer } from '@aytm/app/stats/page/question-views/components/Component'

import { Checkbox } from 'primereact/checkbox'
import { InputNumber } from 'primereact/inputnumber'
import { Dropdown } from 'primereact/dropdown'

import AdvancedFilterContainer from '@aytm/app/stats/page/components/AdvancedFilterContainer'

import AnswerVariant from '@aytm/app/stats/page/question-views/vq/AnswerVariant'
import AnswerVariantMX from '@aytm/app/stats/page/question-views/vq/AnswerVariantMX'
import AnswerVariantOE from '@aytm/app/stats/page/question-views/vq/AnswerVariantOE'
import AnswerVariantOEX from '@aytm/app/stats/page/question-views/vq/AnswerVariantOEX'

import Controls from '@aytm/app/stats/page/question-views/vq/Controls'
import Footer from '@aytm/app/stats/page/question-views/components/component/Footer'

// styles
import style from '@aytm/app/stats/page/question-views/vq/styles/Component.module.scss'

export const DataContext = React.createContext()

const BaseOptions = [
  { text: 'Entire sample (N)', key: 'number' },
  { text: 'Sum of all VQ answers', key: 'sum' },
  { text: 'Custom', key: 'expression' }
]

const getDefaultAnswerVariant = (av = {}) => {
  return {
    key: generateUID(),
    text: '',
    pattern: '',
    base: '',
    mode: 'basic',
    other: false,
    ...av
  }
}

const itemBaseTemplate = option => option.text
const valueBaseTemplate = option => (option ? `Base: ${option.text}` : 'Base: N')

const Component = ({
  model,
  data,
  states,
  disabled,
  isAdmin,
  onChange,
  onControlsChange,
  onUpdateData,
  getTopsOptions,
  getLabelsOptions,
  getImagesOptions,
  getAnswerVariantsOptions
}) => {
  const [state, dispatch] = useReducer(dataReducer, {
    pattern: model.pattern,
    patternType: model.patternType,
    answerVariants: JSON.parse(JSON.stringify(model.answerVariants)),
    logicData: { pattern: [], items: {} },
    refs: {},
    errors: []
  })

  const [changed, setChanged] = useState(false)

  const timer = useRef(null)

  const stateRef = useRef()
  stateRef.current = state

  const dndItemsListRef = useRef()

  const dropdownsRef = useRef({})
  const parserResultPatternBasicsRef = useRef({})

  const mode = useMemo(() => {
    const answerVariants = state.answerVariants.filter(
      (_av, index) => null !== parserResultPatternBasicsRef.current[index + 1]
    )
    return !answerVariants.length || 'mx' == model.variant
      ? null
      : answerVariants.every(av => 'basic' == av.mode)
        ? 'basic'
        : 'advanced'
  }, [model.variant, state])
  const modeDisabled = useMemo(
    () =>
      null === mode ||
      !data.options.some(group => 0 < group.items.length) ||
      !state.answerVariants.some((_av, index) => null !== parserResultPatternBasicsRef.current[index + 1]) ||
      disabled,
    [data.options, mode, state, disabled]
  )

  const AnswerVariantComponent = useMemo(
    () =>
      'oex' == model.variant
        ? AnswerVariantOEX
        : 'oe' == model.variant
          ? AnswerVariantOE
          : 'mx' == model.variant
            ? AnswerVariantMX
            : AnswerVariant,
    [model.variant]
  )

  const answerVariantAmount = useMemo(() => state.answerVariants.length, [state])

  const answerVariantPrefix = useMemo(
    () => (['oe', 'oex'].includes(model.variant) ? 'Q' : 'mx' == model.variant ? 'SQ' : 'A'),
    [model.variant]
  )
  const answerVariantAddText = useMemo(
    () =>
      `Add ${window.NumberToWords(state.answerVariants.length + 1)} ${
        ['oe', 'oex'].includes(model.variant) ? 'Question' : 'mx' == model.variant ? 'Sub-Question' : 'Answer'
      }`,
    [model.variant, state]
  )

  const baseOptions = useMemo(() => {
    if (state.answerVariants.some(av => av.base && 0 < av.base.length && !Number.isNaN(Number(av.base)))) {
      if (['number', 'sum'].includes(state.patternType)) {
        dispatch({
          type: 'UPDATE_PATTERN',
          data: { pattern: '', patternType: null }
        })
      }

      return BaseOptions.map(o => (['number', 'sum'].includes(o.key) ? Object.assign({}, o, { disabled: true }) : o))
    } else if (state.answerVariants.some(av => av.base && 0 < av.base.length)) {
      if (['number'].includes(state.patternType)) {
        dispatch({
          type: 'UPDATE_PATTERN',
          data: { pattern: '', patternType: null }
        })
      }

      return BaseOptions.map(o => (['number'].includes(o.key) ? Object.assign({}, o, { disabled: true }) : o))
    } else {
      return BaseOptions
    }
  }, [state])

  const basicFilters = useMemo(
    () =>
      new BasicFilters({
        clusters: data.clusters,
        surveySchema: data.surveySchema,
        longAllowed: true,
        tagsAllowed: ['rb', 'cb'].includes(model.variant),
        // sentimentsAllowed: ['rb', 'cb'].includes(model.variant),
        isAdmin
      }),
    [model.variant, data.surveySchema, data.clusters, isAdmin]
  )

  const parserResultEof = useMemo(
    () =>
      data.parser.analyzeString({
        filter: '',
        saveTokens: true
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data.surveySchema]
  )
  const parserResultPattern = useMemo(
    () =>
      ('expression' == state.patternType &&
        data.parser.analyzeString({
          id: model.id,
          filter: state.pattern.trim(),
          clusters: data.clusters,
          saveTokens: true
        })) ||
      parserResultEof,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [model.id, data.surveySchema, data.clusters, state.pattern, state.patternType, parserResultEof]
  )
  const parserResultPatternError = useMemo(
    () =>
      'expression' == state.patternType &&
      0 < state.pattern.trim().length &&
      0 < parserResultPattern.expression.status.errors.length &&
      parserResultPattern.expression.status.errors.some(v => !['ref', 'cluster'].includes(v.type)),
    [state.pattern, state.patternType, parserResultPattern]
  )

  const onDNDPositionChange = useCallback(
    ({ newPosition, dragItemUpdater } = {}) => {
      dragItemUpdater(node => {
        const itemPositionNode = node.querySelector('.stats-VQ-AnswerVariant-index')
        if (null !== itemPositionNode) {
          itemPositionNode.innerHTML = null === newPosition ? '' : `${answerVariantPrefix}${newPosition + 1}.`
        }
      })
    },
    [answerVariantPrefix]
  )
  const onDNDUpdateItemPositions = useCallback(
    ({ hiddenItemIndex, placeholderIndex } = {}) => {
      const itemPositionNodes = dndItemsListRef.current.querySelectorAll(
        '.stats-VQ-AnswerVariant .stats-VQ-AnswerVariant-index'
      )

      for (const [i, itemPositionNode] of itemPositionNodes.entries()) {
        if (null !== itemPositionNode) {
          const visualItemIndex = i + (null !== hiddenItemIndex && hiddenItemIndex <= i ? -1 : 0)
          const newItemIndex =
            visualItemIndex + (null !== placeholderIndex && placeholderIndex <= visualItemIndex ? 1 : 0)
          itemPositionNode.innerHTML = null === newItemIndex ? '' : `${answerVariantPrefix}${newItemIndex + 1}.`
        }
      }
    },
    [answerVariantPrefix]
  )

  const onDNDDrop = res => {
    const { item, position, clone } = res

    if (clone) {
      dispatch({
        type: 'ADD_ANSWER_VARIANT',
        data: {
          item: {
            ...getDefaultAnswerVariant(item.item),
            key: generateUID()
          },
          position
        }
      })
    } else if (item.id != model.id) {
      item.remove()
      dispatch({
        type: 'ADD_ANSWER_VARIANT',
        data: {
          item: {
            ...getDefaultAnswerVariant(item.item),
            key: generateUID()
          },
          position
        }
      })
    } else if (item.position != position) {
      dispatch({
        type: 'REORDER_ANSWER_VARIANTS',
        data: {
          from: item.position,
          to: position
        }
      })
    }

    onDNDUpdateItemPositions()
  }

  const dndItemsList = ADND.targets
    .itemsList(
      {
        acceptTypes: [`dlq-answer-${data.windowUID}`, `dlq-answer-other-${model.id}-${data.windowUID}`],
        placeholderVMargin: 10
      },
      { capture: false }
    )
    .buildPlaceholder(() => {
      const placeholderNode = document.createElement('div')
      placeholderNode.className = `${style.placeholder}`
      return placeholderNode
    })
    .onPositionChange(onDNDPositionChange)
    .onUpdateItemPositions(onDNDUpdateItemPositions)
    .onEnter(onDNDUpdateItemPositions)
    .onLeave(onDNDUpdateItemPositions)
    .onDrop(onDNDDrop)

  const checkModel = () => {
    const newData = {}
    if (state.pattern != model.pattern) newData.pattern = model.pattern
    if (state.patternType != model.patternType) newData.patternType = model.patternType
    if (!isEqual(state.answerVariants, model.answerVariants)) {
      newData.logicData = state.logicData
      newData.refs = state.refs
      newData.errors = JSON.parse(JSON.stringify(state.errors))
      for (const avi in state.answerVariants) {
        delete newData.logicData.items[`a${Number(avi) + 1}`]
        delete newData.refs[`a${Number(avi) + 1}`]
        const index = newData.errors.indexOf(`a${Number(avi) + 1}`)
        0 <= index && newData.errors.splice(index, 1)
      }
      newData.answerVariants = JSON.parse(JSON.stringify(model.answerVariants))
    }
    return newData
  }
  const checkState = () => {
    const newData = {}
    if (state.pattern != model.pattern) newData.pattern = state.pattern
    if (state.patternType != model.patternType) newData.patternType = state.patternType
    if (!isEqual(state.answerVariants, model.answerVariants))
      newData.answerVariants = JSON.parse(JSON.stringify(state.answerVariants))
    return newData
  }

  const checkError = () => {
    if (0 < state.errors.length || !state.answerVariants.length) return { error: true }
    if ('mx' == model.variant) {
      if (2 > state.answerVariants.length) return { error: true }
      const l = state.answerVariants.reduce((acc, cur) => {
        const option = data.options.flatMap(v => v.items).find(v => v.key == cur.pattern)
        if (option == undefined) return acc
        return [...acc, option.options.length].filter((v, i, a) => a.indexOf(v) == i)
      }, []).length
      if (1 != l) {
        return { error: 'Each sub-question must have the same number of answer options.' }
      }
    }
    return { error: false }
  }

  const onModeChange = () => {
    dispatch({
      type: 'UPDATE_ANSWER_VARIANTS_MODE',
      data: {
        positions: Object.keys(parserResultPatternBasicsRef.current).filter(
          k => null !== parserResultPatternBasicsRef.current[k]
        ),
        mode: 'basic' == mode ? 'advanced' : 'basic'
      }
    })
  }

  const onAddClick = () => {
    const { answerVariants } = state
    const index = answerVariants.findIndex(av => av.other)

    dispatch({
      type: 'ADD_ANSWER_VARIANT',
      data: {
        item: getDefaultAnswerVariant({ mode: mode || 'basic' }),
        position: 0 <= index && index + 1 == answerVariants.length ? index : answerVariants.length
      }
    })
  }

  const onPatternChange = (value = '') => {
    dispatch({
      type: 'UPDATE_PATTERN',
      data: { pattern: value }
    })
  }

  const onPatternTypeChange = (patternType = null) => {
    dispatch({
      type: 'UPDATE_PATTERN',
      data: { pattern: '', patternType }
    })
  }

  const onOtherChange = () => {
    const answerVariants = state.answerVariants
    const index = answerVariants.findIndex(av => av.other)

    0 > index
      ? dispatch({
          type: 'ADD_ANSWER_VARIANT',
          data: {
            item: getDefaultAnswerVariant({
              key: data.otherUID,
              text: 'Other',
              other: true
            }),
            position: answerVariants.length
          }
        })
      : dispatch({
          type: 'DELETE_ANSWER_VARIANT',
          data: index
        })
  }

  useEffect(() => {
    dispatch({
      type: 'UPDATE_LOGIC_DATA',
      data: {
        id: 'pattern',
        logicData: getLogicData(data.tags, data.codeBooks, parserResultPattern, parserResultPatternError)
      }
    })
  }, [data.tags, data.codeBooks, parserResultPattern, parserResultPatternError])
  useEffect(() => {
    const patternRefs = getRefs(parserResultPattern)
    const clustersRefs = getClustersRefs(data.modelClusters, parserResultPattern)

    dispatch({
      type: 'UPDATE_REFS',
      data: {
        id: `pattern`,
        refs: [patternRefs, clustersRefs]
      }
    })
  }, [data.modelClusters, parserResultPattern])
  useEffect(() => {
    dispatch({
      type: 'UPDATE_ERRORS',
      data: {
        id: 'pattern',
        error: parserResultPatternError
      }
    })
  }, [parserResultPatternError])

  useEffect(() => {
    dndItemsList.connect(dndItemsListRef.current)

    return () => {
      dndItemsList.disconnect()
    }
  }, [dndItemsList])

  useEffect(() => {
    if (changed) return

    dispatch({
      type: 'RESET',
      data: { ...checkModel() }
    })

    setChanged(
      stateRef.current.variant != model.variant ||
        stateRef.current.pattern != model.pattern ||
        stateRef.current.patternType != model.patternType ||
        !isEqual(stateRef.current.answerVariants, model.answerVariants)
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model.pattern, model.patternType, model.answerVariants])

  useEffect(() => {
    setChanged(true)

    const newData = {
      logicData: state.logicData,
      refs: state.refs,
      ...checkError(),
      ...checkState()
    }

    timer.current && clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      if (Object.hasOwn(newData, 'answerVariants')) {
        dispatchEvent(
          new CustomEvent('customQuestionChanged', {
            detail: {
              id: model.id.toUpperCase(),
              items: newData.answerVariants.reduce((acc, cur, i) => {
                switch (model.variant) {
                  case 'mx': {
                    const option = data.options.flatMap(v => v.items).find(v => v.key == cur.pattern)
                    acc[cur.key] = {
                      key: `SQ${i + 1}`,
                      size: option != undefined ? option.options.length : 0
                    }
                    break
                  }
                  default: {
                    acc[cur.key] = `A${i + 1}`
                  }
                }
                return acc
              }, {}),
              delay: newData.answerVariants.length > model.answerVariants.length
            }
          })
        )
      }

      onChange(newData)

      setChanged(false)
    }, 300)

    return () => timer.current && clearTimeout(timer.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])

  const dataContextValue = useMemo(() => {
    return {
      id: model.id,
      data,
      disabled,
      dispatch,
      dropdownsRef,
      parserResultPatternBasicsRef,
      basicFilters,
      parserResultEof,
      answerVariantAmount
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    model.id,
    data,
    disabled,
    dispatch,
    dropdownsRef,
    parserResultPatternBasicsRef,
    basicFilters,
    parserResultEof,
    answerVariantAmount
  ])

  const renderPatternOptions = () => (
    <>
      <div className="p-field p-right p-col">
        <Dropdown
          className="p-dropdown-inline p-dropdown-right"
          placeholder="Base: N"
          value={state.patternType}
          options={baseOptions}
          optionValue="key"
          optionLabel="text"
          itemTemplate={itemBaseTemplate}
          valueTemplate={valueBaseTemplate}
          fixedDropdownDir="bottom"
          disabled={disabled}
          onChange={disabled ? null : event => onPatternTypeChange(event.value)}
        />
      </div>
      <Transition in={['number', 'expression'].includes(state.patternType)} timeout={500}>
        {status => (
          <div
            className={`p-fluid p-field p-right p-col ${style['transition-base']} ${
              status ? style['transition-base-' + status] : ''
            }`}
          >
            <div className="p-field p-col-12">
              {'number' == state.patternType && (
                <InputNumber
                  placeholder=""
                  min={0}
                  mode="decimal"
                  value={state.pattern || ''}
                  showButtons
                  useGrouping={false}
                  disabled={disabled}
                  onChange={disabled ? null : event => onPatternChange(String(event.value || ''))}
                  onBlur={disabled || 0 < state.pattern.trim().length ? null : () => onPatternTypeChange()}
                />
              )}
              {'expression' == state.patternType && (
                <AdvancedFilterContainer
                  className="p-component p-inputtext"
                  pattern={state.pattern || ''}
                  parser={data.parser}
                  locale={data.locale}
                  surveySchema={data.surveySchema}
                  clusters={data.clusters}
                  parserResultEof={parserResultEof}
                  hintPosition="top"
                  disabled={disabled}
                  onChange={disabled ? null : onPatternChange}
                  onBlur={disabled || 0 < state.pattern.trim().length ? null : onPatternTypeChange}
                />
              )}
            </div>
          </div>
        )}
      </Transition>
    </>
  )

  return (
    <ComponentContainer
      Controls={Controls}
      model={model}
      data={data}
      states={states}
      changed={changed}
      disabled={disabled}
      onChange={onControlsChange}
      getTopsOptions={getTopsOptions}
      getLabelsOptions={getLabelsOptions}
      getImagesOptions={getImagesOptions}
      getAnswerVariantsOptions={getAnswerVariantsOptions}
    >
      <div className={style.component}>
        {modeDisabled ? null : (
          <div className={`${style.row} p-field p-col-12`}>
            <span className={style.toggle} onClick={onModeChange}>
              switch ALL to {'basic' == mode ? 'advanced' : 'basic'} mode
            </span>
          </div>
        )}
        <div ref={dndItemsListRef} className={style.list}>
          <DataContext.Provider value={dataContextValue}>
            {state.answerVariants.map((item, index) => (
              <AnswerVariantComponent key={item.key} item={item} position={index + 1} dndItemsList={dndItemsList} />
            ))}
          </DataContext.Provider>
        </div>
        {!state.pattern.length && 'sum' != state.patternType && disabled ? null : (
          <div className={`${style.row} p-fluid p-field p-col-12`}>
            {disabled ? null : (
              <>
                <div className={`${style.index} p-field`}>
                  {answerVariantPrefix}
                  {state.answerVariants.length + 1}.
                </div>
                <div className="p-field">
                  <span className={style.add} onClick={onAddClick}>
                    {answerVariantAddText}
                  </span>
                </div>
                {['mx', 'oe', 'oex'].includes(model.variant) ? null : (
                  <div className={`${style.group} ${style['group-checkbox']} p-field`}>
                    <Checkbox
                      value="other"
                      checked={state.answerVariants.some(av => av.other)}
                      onChange={onOtherChange}
                    />
                    <span className={style['group-checkbox-label']} onClick={onOtherChange}>
                      Everyone else
                    </span>
                  </div>
                )}
              </>
            )}
            {['mx', 'oe', 'oex'].includes(model.variant) ? null : renderPatternOptions()}
          </div>
        )}
      </div>
      {disabled ? null : <Footer model={model} data={data} states={states} onUpdateData={onUpdateData} />}
    </ComponentContainer>
  )
}

Component.propTypes = {
  model: PropTypes.object,
  data: PropTypes.object,
  states: PropTypes.object,
  disabled: PropTypes.bool,
  isAdmin: PropTypes.bool,
  onChange: PropTypes.func,
  onControlsChange: PropTypes.func,
  onUpdateData: PropTypes.func,
  getTopsOptions: PropTypes.func,
  getLabelsOptions: PropTypes.func,
  getImagesOptions: PropTypes.func,
  getAnswerVariantsOptions: PropTypes.func
}

Component.displayName = 'Component'

export default Component

ADND().connect()
